import { AppConstants } from "@/helpers/app.constants";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import { EmailWithTLDValidator } from "@/validators/EmailWithTLDValidator";
import { PhoneNumberValidator } from "@/validators/PhoneNumberValidator";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import {
  ErrorBusinessCode,
  User,
  UserRole,
  UsersService,
} from "@eemo/api-client";
import { MdbPopoverModule } from "mdb-angular-ui-kit/popover";
import { PropertySelectDialogComponent } from "../property-select-dialog/property-select-dialog.component";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { PostalCodeValidator } from "@/validators/PostalCodeValidator";
import { NgIf } from "@angular/common";
import { MatOptionModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { TranslateModule } from "@ngx-translate/core";
import { InputTrimDirective } from "../../extensions/inputTrimDirective";

export class UserDialogData {
  user: User;
  edition = false;
}

@Component({
  selector: "app-user-dialog",
  templateUrl: "./user-dialog.component.html",
  styleUrls: ["./user-dialog.component.scss"],
  standalone: true,
  imports: [
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    TooltipDirective,
    MatInputModule,
    InputTrimDirective,
    NgIf,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    MatCheckboxModule,
    MdbPopoverModule,
    MatButtonModule,
    TranslateModule,
  ],
})
export class UserDialogComponent implements OnInit {
  userForm: UntypedFormGroup;
  addressForm: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;

  showAddress = false;
  hidePassword = true;
  hidePasswordConfirm = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UserDialogData,
    private userService: UsersService,
    private fb: UntypedFormBuilder,
    private alertService: AlertService,
    public dialogRef: MatDialogRef<UserDialogComponent>,
    public userHelper: UserHelper,
    private dialog: MatDialog
  ) {
    this.dialogTitle = this.data.edition
      ? "Modifier l'utilisateur"
      : "Créer un nouvel utilisateur";
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.userForm.get(field);
  }
  fAddr(field: string): AbstractControl {
    return this.userForm.get("address").get(field);
  }

  ngOnInit() {
    this.userForm = this.fb.group({
      role: [UserRole.INDIVIDUAL, Validators.required],
      name: ["", [Validators.required, Validators.maxLength(50)]],
      firstName: ["", [Validators.required, Validators.maxLength(50)]],
      email: [
        { value: "", disabled: this.data.edition },
        [Validators.required, EmailWithTLDValidator],
      ],
      phoneNumber: [null, PhoneNumberValidator],
      sendInformations: [false],
      password: ["", [Validators.pattern(AppConstants.FORM_PATTERN_PASSWORD)]],
      passwordConfirmed: [""],
    });

    this.addressForm = this.fb.group({
      street: ["", [Validators.required, Validators.maxLength(255)]],
      complement: ["", [Validators.maxLength(255)]],
      postalCode: ["", [Validators.required, PostalCodeValidator]],
      city: ["", [Validators.required, Validators.maxLength(255)]],
      country: ["", Validators.maxLength(255)],
    });

    this.f("sendInformations").valueChanges.subscribe((value) => {
      if (value) {
        this.f("password").disable();
        this.f("passwordConfirmed").disable();
      } else {
        this.f("password").enable();
        this.f("passwordConfirmed").enable();
      }
    });

    if (this.data.user) {
      if (this.data.user.address) {
        this.showAddress = true;
      }
      this.manageAddressForm();
      this.userForm.patchValue(this.data.user);
    }
    if (!this.data.edition) {
      this.f("password").setValidators([
        Validators.required,
        Validators.pattern(AppConstants.FORM_PATTERN_PASSWORD),
      ]);
      this.f("passwordConfirmed").setValidators([Validators.required]);
    }
  }

  onSubmit() {
    if (this.f("password").value !== this.f("passwordConfirmed").value) {
      this.f("password").setErrors({ passwordDontMatch: true });
      this.f("passwordConfirmed").setErrors({ passwordDontMatch: true });
    } else {
      this.f("password").setErrors({});
      this.f("password").updateValueAndValidity({ onlySelf: false });
      this.f("passwordConfirmed").setErrors({});
      this.f("passwordConfirmed").updateValueAndValidity({ onlySelf: false });
    }

    // replace spaces by - in names
    this.f("firstName").setValue(
      this.f("firstName").value.trim().replace(" ", "-")
    );
    // force uppercase
    this.f("name").setValue(
      this.f("name").value.trim().replace(" ", "-").toUpperCase()
    );

    if (this.userForm.valid) {
      this.formSubmitting = true;
      if (this.data.edition) {
        this.userService
          .updateUser(this.data.user.uid, this.userForm.getRawValue())
          .subscribe({
            next: () => {
              if (this.f("password").value) {
                this.userService
                  .changeUserPassword(this.data.user.uid, {
                    oldPassword: "xxx",
                    newPassword: this.f("password").value,
                  })
                  .subscribe({
                    next: () => {
                      this.dialogRef.close(true);
                      this.alertService.success(
                        "Modification de l'utilisateur effectuée"
                      );
                    },
                    error: (err: HttpErrorResponse) => {
                      if (err.error && err.error.businessCode) {
                        switch (err.error.businessCode) {
                          case ErrorBusinessCode.REFUSED_PASSWORD:
                            this.f("password").setErrors({
                              refusedPassword: true,
                            });
                            break;
                        }
                        this.formSubmitting = false;
                      } else {
                        this.dialogRef.close(false);
                      }
                    },
                  });
              } else {
                this.dialogRef.close(true);
                this.alertService.success(
                  "Modification de l'utilisateur effectuée"
                );
              }
            },
            error: () => {
              this.dialogRef.close(false);
            },
          });
      } else {
        const sendInfos = this.f("sendInformations").value;
        this.userService
          .createUser(this.userForm.getRawValue(), sendInfos)
          .subscribe(
            (createdId) => {
              if (sendInfos) {
                this.dialogRef.close(true);
                this.alertService.success(
                  "Création de l'utilisateur effectuée"
                );
              } else {
                this.userService
                  .changeUserPassword(createdId, {
                    oldPassword: "xxx",
                    newPassword: this.f("password").value,
                  })
                  .subscribe({
                    next: () => {
                      this.dialogRef.close(true);
                      this.alertService.success(
                        "Création de l'utilisateur effectuée"
                      );
                    },
                    error: (err: HttpErrorResponse) => {
                      if (err.error && err.error.businessCode) {
                        this.alertService.error(
                          "Erreur de création du mot de passe : mot de passe trop simpliste. Veuillez le redéfinir."
                        );
                      }
                      this.dialogRef.close(true);
                    },
                  });
              }
            },
            (err: HttpErrorResponse) => {
              if (
                err.error &&
                err.error.businessCode &&
                err.error.businessCode === ErrorBusinessCode.EMAIL_EXISTS
              ) {
                this.f("email").setErrors({ exists: true });
                this.formSubmitting = false;
              } else if (
                err.error &&
                err.error.businessCode &&
                err.error.businessCode ===
                  ErrorBusinessCode.USER_ACCOUNT_NOT_MANAGED
              ) {
                this.f("email").setErrors({ notManaged: true });
                this.formSubmitting = false;
              }
            }
          );
      }
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }

  manageAddressForm() {
    if (this.showAddress) {
      this.userForm.addControl("address", this.addressForm);
    } else {
      this.userForm.removeControl("address");
    }
  }

  toggleShowAddress() {
    this.showAddress = !this.showAddress;
    this.manageAddressForm();
  }

  fillAddressFromProperty() {
    const dialogRef = this.dialog.open(PropertySelectDialogComponent, {
      data: { ownerOnly: false, targetUserId: this.data.user.uid },
    });

    dialogRef.afterClosed().subscribe((prop) => {
      if (prop && prop.address) {
        this.fAddr("street").setValue(prop.address.street);
        this.fAddr("complement").setValue(prop.address.complement);
        this.fAddr("postalCode").setValue(prop.address.postalCode);
        this.fAddr("city").setValue(prop.address.city);
        this.fAddr("country").setValue(prop.address.country);
      }
    });
  }
}
